import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import queryString from "query-string";
import { Button, Link, Spinner, Title } from "@yoast/ui-library";
import { UserCircleIcon } from "@heroicons/react/24/solid";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { hasAccessToken } from "shared-frontend/functions/auth";
import { getUserEmail, getUserFirstName, getUserLastName } from "shared-frontend/redux/selectors/user/data/profile";
import useOAuthInteraction from "../../reactHooks/useOAuthInteraction";
import yoastIcon from "../../icons/yoast-icon.svg";
import ConsentCredentialReauth from "./ConsentCredentialReauth";
import * as styles from "./ConsentScreenStyles.module.scss";

/* eslint-disable complexity */
/**
 * OAuth Consent Screen component.
 *
 * Displays the authorization request to the user, showing:
 * - Client application info (name, logo)
 * - Logged-in user info
 * - Requested permissions (scopes)
 * - Authorize/Cancel buttons
 *
 * @param {Object} props The component props.
 * @param {Object} props.location The router location object.
 *
 * @returns {React.ReactElement} The consent screen component.
 */
function ConsentScreen( { location } ) {
	const [ logoFailed, setLogoFailed ] = useState( false );

	// Get logged-in user info from Redux
	const userEmail = useSelector( getUserEmail );
	const userFirstName = useSelector( getUserFirstName );
	const userLastName = useSelector( getUserLastName );
	const userName = [ userFirstName, userLastName ].filter( Boolean ).join( " " );

	// Parse the interaction UID from query params
	const queryParams = queryString.parse( location.search );
	const interactionUid = queryParams.uid;

	// Use the OAuth interaction hook for state and API calls
	const {
		interactionData,
		loading,
		error,
		submitting,
		redirecting,
		redirectFailed,
		redirectUrl,
		redirectContext,
		requiresCredentialReauth,
		fetchInteractionDetails,
		authorize,
		cancel,
		resolveInteractionAfterLogin,
	} = useOAuthInteraction( interactionUid );

	// Check auth and fetch data on mount
	useEffect( () => {
		fetchInteractionDetails();
	}, [ fetchInteractionDetails ] );

	// Handle browser back/forward navigation
	// When the user navigates back after completing the flow, we need to re-validate the interaction
	useEffect( () => {
		/**
		 * Handles the pageshow event to detect back-forward cache restoration.
		 *
		 * @param {PageTransitionEvent} event The pageshow event.
		 *
		 * @returns {void}
		 */
		const handlePageShow = ( event ) => {
			// event.persisted is true when the page is restored from the back-forward cache
			if ( event.persisted ) {
				fetchInteractionDetails();
			}
		};

		window.addEventListener( "pageshow", handlePageShow );
		return () => {
			window.removeEventListener( "pageshow", handlePageShow );
		};
	}, [ fetchInteractionDetails ] );

	/**
	 * Gets the first letter of the client name for the placeholder logo.
	 *
	 * @param {string} name The client name.
	 *
	 * @returns {string} The first letter uppercase.
	 */
	const getClientInitial = ( name ) => {
		return name ? name.charAt( 0 ).toUpperCase() : "?";
	};

	/**
	 * Extracts the hostname from a URL.
	 *
	 * @param {string} url The URL to parse.
	 *
	 * @returns {string} The hostname or the original URL if parsing fails.
	 */
	const getHostname = ( url ) => {
		try {
			return new URL( url ).hostname;
		} catch {
			return url;
		}
	};

	// Show loading state
	if ( loading ) {
		return (
			<div className={ styles.pageWrapper }>
				<div className={ styles.card }>
					<div className={ styles.loadingContainer }>
						<Spinner variant="primary" size={ "8" } />
						<p className="yst-text-slate-500 yst-mt-4">
							Loading authorization request...
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Show redirecting state
	if ( redirecting ) {
		return (
			<div className={ styles.pageWrapper }>
				<div className={ styles.card }>
					<div className={ styles.loadingContainer }>
						{ ! redirectFailed && (
							<>
								<Spinner variant="primary" size={ "8" } />
								<p className="yst-text-slate-500 yst-mt-4">
									Redirecting to { redirectContext }...
								</p>
							</>
						) }
						{ redirectFailed && (
							<>
								<Title as="h1" size={ "3" } className={ styles.title }>
									Redirect failed
								</Title>
								{ redirectUrl ? (
									<>
										<p className="yst-text-slate-500 yst-mt-2 yst-mb-4">
											The automatic redirect to { redirectContext } didn&apos;t work.
											Please click the link below to continue:
										</p>
										<Link href={ redirectUrl }>
											Click here to { redirectContext }
										</Link>
									</>
								) : (
									<p className="yst-text-slate-500 yst-mt-2">
										Something went wrong. Please go back to the application and try again.
									</p>
								) }
							</>
						) }
					</div>
				</div>
			</div>
		);
	}

	// Render the in-consent credential form when the user isn't logged in yet
	// OR when the OIDC client requested `prompt=login`. We never leave the
	// consent page in either case, which keeps any later JWT cookie present
	// and avoids the silent-login iframe in index.jsx.
	if ( ! hasAccessToken() || requiresCredentialReauth ) {
		return (
			<div className={ styles.pageWrapper }>
				<div className={ styles.card }>
					{ /* Resolver error (e.g. :uid/login returns no redirectTo) is
					     surfaced above the credential form. The form owns its own
					     submit-time errors (bad password, 2FA failure, etc.). */ }
					{ error && (
						<div className={ styles.errorMessage } role="alert">
							{ error.message }
						</div>
					) }
					<ConsentCredentialReauth
						clientName={ interactionData?.client?.name ?? null }
						onAuthenticated={ resolveInteractionAfterLogin }
					/>
				</div>
			</div>
		);
	}

	// Show error state if no data
	if ( ! interactionData ) {
		return (
			<div className={ styles.pageWrapper }>
				<div className={ styles.card }>
					<Title as="h1" size={ "2" } className={ styles.title }>
						Authorization Failed
					</Title>
					<div className={ styles.errorMessage } role="alert">
						{ error?.message || "Unable to load the authorization request. Please go back to the application and try again." }
					</div>
				</div>
			</div>
		);
	}

	const { client, scopes, redirectUri } = interactionData;

	/**
	 * Renders the client logo or a placeholder with the initial.
	 *
	 * @returns {React.ReactElement} The client logo element.
	 */
	const renderClientLogo = () => {
		if ( client.logoUri && ! logoFailed ) {
			return (
				<img
					src={ client.logoUri }
					alt={ `${ client.name } logo` }
					className={ styles.logoImage }
					onError={ () => setLogoFailed( true ) }
				/>
			);
		}
		return (
			<span className={ styles.logoInitial }>
				{ getClientInitial( client.name ) }
			</span>
		);
	};

	return (
		<div className={ styles.pageWrapper }>
			<div className={ styles.card }>
				{ /* Logo connection with dotted line */ }
				<div className={ styles.logoConnection }>
					<div className={ styles.logoBox }>
						{ renderClientLogo() }
					</div>
					<div className={ styles.connectionLine } />
					<div className={ styles.logoBox }>
						<img
							src={ yoastIcon }
							alt="MyYoast"
							className={ styles.yoastLogoImage }
						/>
					</div>
				</div>

				{ /* Title */ }
				<Title as="h1" size={ "3" } className={ styles.title }>
					<strong>{ client.name }</strong> wants access to your MyYoast account
				</Title>

				{ /* Logged-in user card */ }
				<div className={ styles.userCard }>
					<UserCircleIcon className={ styles.userAvatar } aria-hidden="true" />
					<div className={ styles.userInfo }>
						<span className={ styles.userName }>{ userName || "User" }</span>
						<span className={ styles.userEmail }>{ userEmail }</span>
					</div>
				</div>

				{ /* Error message */ }
				{ error && (
					<div className={ styles.errorMessage } role="alert">
						{ error.message }
					</div>
				) }

				{ /* Permissions section */ }
				<div className={ styles.permissionsSection }>
					<p className={ styles.permissionsLabel }>
						This will allow { client.clientUri ? (
							<Link href={ client.clientUri } target="_blank" rel="noopener noreferrer">
								{ client.name }
							</Link>
						) : client.name } access to:
					</p>

					<ul className={ styles.scopesList } aria-label="Requested permissions">
						{ scopes.map( ( scope ) => (
							<li key={ scope.scope } className={ styles.scopeItem }>
								<ChevronRightIcon className={ styles.scopeIcon } aria-hidden="true" />
								<div className={ styles.scopeText }>
									<span className={ styles.scopeTitle }>{ scope.title }</span>
									{ scope.description && (
										<span className={ styles.scopeDescription }>{ scope.description }</span>
									) }
								</div>
							</li>
						) ) }
					</ul>
				</div>

				{ /* Legal notice above buttons */ }
				{ ( client.tosUri || client.policyUri ) && (
					<p className={ styles.legalNotice }>
						By clicking &apos;Authorize&apos;, you agree to { client.name }&apos;s{ " " }
						{ client.tosUri && (
							<Link href={ client.tosUri } target="_blank" rel="noopener noreferrer">
								Terms of Use
							</Link>
						) }
						{ client.tosUri && client.policyUri && " and " }
						{ client.policyUri && (
							<Link href={ client.policyUri } target="_blank" rel="noopener noreferrer">
								Privacy Policy
							</Link>
						) }.
					</p>
				) }

				{ /* Action buttons - side by side */ }
				<div className={ styles.actions }>
					<Button
						variant="secondary"
						className={ styles.cancelButton }
						onClick={ cancel }
						disabled={ submitting }
					>
						Cancel
					</Button>
					<Button
						className={ styles.authorizeButton }
						onClick={ authorize }
						disabled={ submitting }
						isLoading={ submitting }
					>
						Authorize
					</Button>
				</div>

				{ /* Redirect notice */ }
				<p className={ styles.redirectNotice }>
					You&apos;ll be redirected to { getHostname( redirectUri ) }.
				</p>
			</div>
		</div>
	);
}
/* eslint-enable complexity */

ConsentScreen.propTypes = {
	location: PropTypes.shape( {
		search: PropTypes.string,
	} ).isRequired,
};

export default ConsentScreen;
